import { useState } from 'react'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import type { AspectRatio, BrandPreset } from '../../client'

export type Resolution = '1k' | '2k' | '4k'

const MAX_LENGTH = 2000
const COUNTER_FROM = 1800
const ASPECT_OPTIONS: { value: AspectRatio; label: string }[] = [
  { value: '1:1', label: '1:1 Square' },
  { value: '4:5', label: '4:5 Portrait' },
  { value: '9:16', label: '9:16 Story' },
]
const RESOLUTIONS: Resolution[] = ['1k', '2k', '4k']
const VERSIONS = [1, 2, 3, 4]

const PILL = 'group flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition'
const PILL_IDLE = 'border-gray-200 text-gray-500 hover:border-gray-300'
const PILL_ON = 'border-purple-900 bg-purple-50 text-purple-900'
const CHEVRON = 'size-3.5 transition-transform group-data-open:rotate-180'
const PANEL =
  'z-[60] divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white py-1 shadow-lg [--anchor-gap:6px]'
const OPTION =
  'w-full cursor-pointer px-3 py-1.5 text-left text-xs whitespace-nowrap text-gray-700 transition hover:bg-gray-50 data-selected:bg-purple-50 data-selected:text-purple-700'

type Props = {
  prompt: string
  onPromptChange: (prompt: string) => void
  aspectRatio: AspectRatio
  onAspectRatioChange: (ratio: AspectRatio) => void
  resolution: Resolution
  onResolutionChange: (resolution: Resolution) => void
  versions?: number
  onVersionsChange?: (versions: number) => void
  creditsRemaining?: number
  presets?: BrandPreset[]
  selectedImageUrls?: string[]
  placeholder: string
  title?: string
}

export default function AdPromptInput({
  prompt,
  onPromptChange,
  aspectRatio,
  onAspectRatioChange,
  resolution,
  onResolutionChange,
  versions,
  onVersionsChange,
  creditsRemaining = Infinity,
  presets = [],
  selectedImageUrls = [],
  placeholder,
  title = 'Describe your Ad',
}: Props) {
  const [activePreset, setActivePreset] = useState<number | null>(null)

  function pickPreset(index: number) {
    if (activePreset === index) {
      setActivePreset(null)
      onPromptChange('')
      return
    }

    setActivePreset(index)
    onPromptChange(presets[index]?.prompt ?? '')
  }

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-gray-700">{title}</h3>

      <div className="rounded-lg border border-gray-300 focus-within:border-transparent focus-within:ring-2 focus-within:ring-purple-900">
        {presets.length > 0 && (
          <div className="flex gap-1.5 overflow-x-auto px-3 pt-2.5 [scrollbar-width:none]">
            {presets.map((preset, index) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => pickPreset(index)}
                className={`flex shrink-0 items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium whitespace-nowrap transition ${
                  activePreset === index
                    ? 'border-purple-900 bg-purple-50 text-purple-900'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {preset.label}
                {activePreset === index && <XMarkIcon className="size-3 opacity-70 hover:opacity-100" />}
              </button>
            ))}
          </div>
        )}

        <textarea
          value={prompt}
          rows={4}
          maxLength={MAX_LENGTH}
          placeholder={placeholder}
          onChange={(event) => onPromptChange(event.target.value)}
          className={`w-full resize-none px-3 pt-2.5 pb-1 text-sm focus:outline-none ${
            presets.length > 0 ? '' : 'rounded-t-lg'
          }`}
        />

        <div className="flex items-center justify-between gap-2 px-3 pb-2.5">
          {selectedImageUrls.length > 0 ? (
            <div className="flex items-center gap-1.5">
              <div className="flex items-center -space-x-2 py-1">
                {selectedImageUrls.slice(0, 3).map((url, index) => (
                  <img
                    key={url}
                    src={url}
                    alt=""
                    style={{ transform: `rotate(${(index - 1) * 6}deg)`, zIndex: 3 - index }}
                    className="relative size-6 rounded border border-white object-cover shadow-sm"
                  />
                ))}
                {selectedImageUrls.length > 3 && (
                  <span className="relative z-0 flex size-6 items-center justify-center rounded border border-white bg-gray-100 text-[10px] font-semibold text-gray-500 shadow-sm">
                    +{selectedImageUrls.length - 3}
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-400">{selectedImageUrls.length} selected</span>
            </div>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-2">
            <Listbox value={aspectRatio} onChange={onAspectRatioChange}>
              <ListboxButton className={({ open }) => `${PILL} ${open ? PILL_ON : PILL_IDLE}`}>
                Aspect Ratio: {aspectRatio}
                <ChevronDownIcon className={CHEVRON} />
              </ListboxButton>
              <ListboxOptions anchor="top end" className={PANEL}>
                {ASPECT_OPTIONS.map(({ value, label }) => (
                  <ListboxOption key={value} value={value} className={OPTION}>
                    {label}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Listbox>

            <Listbox value={resolution} onChange={onResolutionChange}>
              <ListboxButton className={({ open }) => `${PILL} ${open ? PILL_ON : PILL_IDLE}`}>
                Resolution: {resolution.toUpperCase()}
                <ChevronDownIcon className={CHEVRON} />
              </ListboxButton>
              <ListboxOptions anchor="top end" className={PANEL}>
                {RESOLUTIONS.map((option) => (
                  <ListboxOption key={option} value={option} className={OPTION}>
                    {option.toUpperCase()}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Listbox>

            {versions !== undefined && onVersionsChange && (
              <div className="flex items-center gap-0.5 rounded-full border border-gray-200 px-1 py-1">
                {VERSIONS.map((count) => (
                  <button
                    key={count}
                    type="button"
                    disabled={count > creditsRemaining}
                    title={count > creditsRemaining ? `Not enough credits (${creditsRemaining} remaining)` : undefined}
                    onClick={() => onVersionsChange(count)}
                    className={`size-7 rounded-full text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-30 ${
                      versions === count ? 'bg-purple-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {count}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {prompt.length >= COUNTER_FROM && (
          <div className="px-3 pb-1 text-right">
            <span
              className={`text-[10px] ${prompt.length >= MAX_LENGTH ? 'font-medium text-red-500' : 'text-gray-400'}`}
            >
              {prompt.length} / {MAX_LENGTH}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
